import {
  HHStack,
  HHTableCell,
  HHTableRow,
} from '@hinge-health/react-component-library';
import { Link } from '@mui/material';
import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  buildConfigurationDetailPath,
  CONFIGURATION_STATUS_LABELS,
} from '../constants/partner-data-gateway.constant';
import { useBasePath } from '../context/base-path-context';
import { ConfigurationStatus } from '../domain/enums';
import { Configuration } from '../domain/types';
import { ConfigurationStatusActions } from './configuration-status-actions';
import { RelativeTimeCell } from './relative-time-cell';
import { StatusChip, STATUS_CHIP_TEST_ID } from './status-chip';
import { TableCard, tableCardTestIds } from './table-card';

const TEST_ID_PREFIX = 'pdg-configurations-table';
const ids = tableCardTestIds(TEST_ID_PREFIX);

export const CONFIGURATIONS_TABLE_TEST_ID = ids.table;
export const CONFIGURATIONS_TABLE_EMPTY_TEST_ID = ids.empty;
export const CONFIGURATIONS_TABLE_SKELETON_TEST_ID = ids.skeleton;
export const CONFIGURATIONS_TABLE_ERROR_TEST_ID = ids.error;
export const CONFIGURATIONS_TABLE_FOOTER_TEST_ID = ids.footer;
export const CONFIGURATIONS_TABLE_TOOLBAR_TEST_ID = ids.toolbar;

export const CONFIGURATIONS_TABLE_EMPTY_MESSAGE =
  'No configurations match these filters. Clear a filter or create a configuration.';
export const CONFIGURATIONS_TABLE_ERROR_MESSAGE =
  'Something went wrong loading configurations.';

export const configurationRowTestId = (id: string): string =>
  `pdg-configurations-table-row-${id}`;
export const configurationNameLinkTestId = (id: string): string =>
  `pdg-configurations-table-name-${id}-link`;
export const configurationEditLinkTestId = (id: string): string =>
  `pdg-configurations-table-edit-${id}-link`;
export const configurationStatusActionsTestId = (id: string): string =>
  `pdg-configurations-table-status-${id}`;
/**
 * The status filter renders an `<option>` for every status label, so the
 * words "Draft" / "Published" / "Deleted" are always on the page. Tests have
 * to target the row's chip by id rather than by text, or they assert against
 * the filter and pass even when the row never renders.
 */
export const configurationStatusChipTestId = (id: string): string =>
  `${STATUS_CHIP_TEST_ID}-${id}`;

const COLUMN_HEADERS = [
  'Name',
  'Provider',
  'Status',
  'Created',
  'Updated',
  'Actions',
];

export interface ConfigurationsTableProps {
  configurations: Configuration[];
  /** Whether to offer the edit link and the status transition buttons. */
  canManage: boolean;
  onStatusChange: (
    configuration: Configuration,
    target: ConfigurationStatus,
  ) => void;
  disabled?: boolean;
  /** True while a fetch is in flight. Only blanks the table on first load. */
  isLoading?: boolean;
  /** True when the last fetch failed. Rows already loaded stay visible. */
  hasError?: boolean;
  /** Filter controls rendered in a row above the column headers. */
  toolbar?: ReactNode;
  /** Rendered on the right of the footer, next to the "Showing N" count. */
  footer?: ReactNode;
}

interface ConfigurationRowProps {
  configuration: Configuration;
  canManage: boolean;
  disabled: boolean;
  onStatusChange: ConfigurationsTableProps['onStatusChange'];
}

const ConfigurationRow = ({
  configuration,
  canManage,
  disabled,
  onStatusChange,
}: ConfigurationRowProps): JSX.Element => {
  const basePath = useBasePath();
  const detailPath = `${basePath}/${buildConfigurationDetailPath(
    configuration.id,
  )}`;

  return (
    <HHTableRow hover data-testid={configurationRowTestId(configuration.id)}>
      <HHTableCell>
        <Link
          component={RouterLink}
          to={detailPath}
          underline="hover"
          color="text.primary"
          variant="body2"
          sx={{ fontWeight: 500 }}
          data-testid={configurationNameLinkTestId(configuration.id)}
        >
          {configuration.name}
        </Link>
      </HHTableCell>
      <HHTableCell>{configuration.dataProvider}</HHTableCell>
      <HHTableCell>
        <StatusChip
          status={configuration.status}
          label={CONFIGURATION_STATUS_LABELS[configuration.status]}
          testId={configurationStatusChipTestId(configuration.id)}
        />
      </HHTableCell>
      <HHTableCell>
        <RelativeTimeCell isoDate={configuration.createdAt} />
      </HHTableCell>
      <HHTableCell>
        <RelativeTimeCell isoDate={configuration.updatedAt} />
      </HHTableCell>
      <HHTableCell>
        <HHStack direction="row" gap={2} alignItems="center">
          {canManage && (
            <Link
              component={RouterLink}
              to={detailPath}
              underline="hover"
              variant="body2"
              sx={{ fontWeight: 500 }}
              data-testid={configurationEditLinkTestId(configuration.id)}
            >
              Edit
            </Link>
          )}
          {canManage && (
            <ConfigurationStatusActions
              status={configuration.status}
              disabled={disabled}
              testIdPrefix={configurationStatusActionsTestId(configuration.id)}
              onSelect={(target): void => onStatusChange(configuration, target)}
            />
          )}
        </HHStack>
      </HHTableCell>
    </HHTableRow>
  );
};

export const ConfigurationsTable = ({
  configurations,
  canManage,
  onStatusChange,
  disabled = false,
  isLoading = false,
  hasError = false,
  toolbar,
  footer,
}: ConfigurationsTableProps): JSX.Element => (
  <TableCard
    testIdPrefix={TEST_ID_PREFIX}
    ariaLabel="Configurations"
    headers={COLUMN_HEADERS}
    count={configurations.length}
    noun={{ singular: 'configuration', plural: 'configurations' }}
    emptyMessage={CONFIGURATIONS_TABLE_EMPTY_MESSAGE}
    errorMessage={CONFIGURATIONS_TABLE_ERROR_MESSAGE}
    isLoading={isLoading}
    hasError={hasError}
    toolbar={toolbar}
    footer={footer}
  >
    {configurations.map(configuration => (
      <ConfigurationRow
        key={configuration.id}
        configuration={configuration}
        canManage={canManage}
        disabled={disabled}
        onStatusChange={onStatusChange}
      />
    ))}
  </TableCard>
);
